import {
    Box,
    Skeleton,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    tableSortLabelClasses,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import { Order, TableHeadComponentProps, TableHeadLoaderProps, TableHeaderData } from "../../types";
import { InvertedTriangleIcon } from "../../../../assets/SvgIcons";
import { visuallyHidden } from "@mui/utils";


const TableHeadLoader = ({
    rowCustomStyles,
    colsNum
}: TableHeadLoaderProps) => {
    const theme = useTheme()

    return (
        <>
            <TableRow
                sx={{
                    '& td': {
                        paddingTop: "15px",
                        paddingBottom: "15px",
                        borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                    },
                    ...rowCustomStyles
                }}
            >
                {[...Array(colsNum)].map((_, index) => (
                    <TableCell key={index} width="540px">
                        <Skeleton animation="wave" variant="text" />
                    </TableCell>
                ))}
            </TableRow>
        </>
    )
};

export default function TableHeadComponent(
    {
        isLoading,
        tableHeadRowCustomStyles,
        tableHeadFontStyles,
        tableHeaderData,
        handleSort,
        tableDataCount,
        order,
        orderBy
    }: TableHeadComponentProps
): JSX.Element {
    const theme = useTheme()

    const onSortIconClick = (
        id: string
    ) => {
        const isAsc = orderBy === id && order === "asc";

        handleSort(id, isAsc ? "desc" : "asc")
    };

    const hasTableHeadFontStyles = tableHeadFontStyles && Object.keys(tableHeadFontStyles).length > 0;


    return (
        <TableHead>
            {isLoading && (
                <TableHeadLoader
                    rowCustomStyles={tableHeadRowCustomStyles}
                    colsNum={tableHeaderData.length}
                />
            )}
            {!isLoading && tableDataCount > 0 && (
                <TableRow
                    sx={{
                        '& th': {
                            paddingTop: "15px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        },
                        ...tableHeadRowCustomStyles
                    }}
                >
                    {tableHeaderData.map((currentHeader: TableHeaderData) => {
                        const labelParts = (currentHeader?.label ?? '').split(' / ').map((part) => part.trim());
                        const isStacked = labelParts.length === 2;

                        const headerLabel = isStacked ? (
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    flexDirection: "column",
                                    alignItems: currentHeader?.align === "left" ? "flex-start" : "center",
                                    rowGap: "4px",
                                }}
                            >
                                <Box component="span">
                                    {labelParts[0].toUpperCase()}
                                </Box>
                                <Box
                                    sx={{
                                        width: "100%",
                                        borderTop: `0.5px solid ${theme.palette.text.secondary}B2`,
                                    }}
                                />
                                <Box component="span">
                                    {labelParts[1].toUpperCase()}
                                </Box>
                            </Box>
                        ) : (
                            currentHeader?.label ? currentHeader.label.toUpperCase() : ''
                        );

                        return (
                        <TableCell
                            key={currentHeader?.id}
                            align={currentHeader?.align ?? "left"}
                            sx={currentHeader?.headerTableCellStyle ?? {}}
                        >
                            {currentHeader.isSortable ? (
                                <TableSortLabel
                                    active={orderBy === currentHeader.id}
                                    direction={orderBy === currentHeader.id ? order : "asc"}
                                    onClick={() => !isLoading && onSortIconClick(currentHeader.id)}
                                    IconComponent={InvertedTriangleIcon}
                                    sx={{
                                        [`.${tableSortLabelClasses.icon}`]: {
                                            opacity: orderBy === currentHeader.id ? 1 : 0.2,
                                            transition: 'opacity 120ms ease',
                                        },
                                    }}
                                >
                                    <Typography
                                        component="div"
                                        {...(!hasTableHeadFontStyles && { variant: "subtitle_500" })}
                                        sx={({
                                            color: theme.palette.text.secondary,
                                            letterSpacing: '0.02px',
                                            lineHeight: '17px',
                                            ...tableHeadFontStyles
                                        })}
                                    >
                                        {headerLabel}
                                        {orderBy === currentHeader.id ? (
                                            <Box component="span" sx={visuallyHidden}>
                                                {order === "desc" ? "sorted descending" : "sorted ascending"}
                                            </Box>
                                        ) : null}
                                    </Typography>
                                </TableSortLabel>
                            ) : (
                                <Typography
                                    component="div"
                                    {...(!hasTableHeadFontStyles && { variant: "subtitle_500" })}
                                    sx={({
                                        color: theme.palette.text.secondary,
                                        ...tableHeadFontStyles
                                    })}
                                >
                                    {headerLabel}
                                </Typography>
                            )}
                        </TableCell>
                    );
                    })}
                </TableRow>
            )}
        </TableHead >
    )
}